import { Divider, Grid, Paper, Tab, Tabs, Typography, Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import BackdropLoading from 'components/common/BackdropLoading';
import { ModalCreateAssociation } from 'components/modal-create-association';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import { GQLGetParty } from 'graphql/parties/queries';
import { TFunction } from 'i18next';
import React, { FC, useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UseQueryResult } from 'react-query';
import { CONTACT_TITLE_AGENCY, CONTACT_TITLE_COMPANY_KIND } from 'utils/constants';
import { NewUserType } from 'v2-types/user';
import { getContactErrorMessage } from 'views/contacts/services/errors';
import { useTranslation } from 'react-i18next';

import Header from './components/header';
import ModalInviteUser from './components/modal-invite-user';
import Navigation from './components/navigation';
import ContactFormEdit from '../contacts/components/contact-form-edit/contact-form-edit';

const useStyles = makeStyles((theme: any) => ({
  container: { padding: theme.spacing(2) },
  errorIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  tabsContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  tab: { textTransform: 'capitalize' },
  formContainer: { padding: theme.spacing(2) },
  title: {
    color: theme.palette.tab.offselected,
    fontWeight: 500,
  },
  innerFormContainer: { marginBottom: theme.spacing(2) },
  formDivider: {
    borderLeftWidth: 1,
    borderLeftStyle: 'solid',
    borderLeftColor: theme.palette.grey[200],
  },
}));

type ContactEditSceneProps = {
  t: TFunction;
  loading: boolean;
  isDeletingParty: boolean;
  methods: UseFormReturn<NewUserType>;
  errors: string[];
  modalInvite: boolean;
  confirmModalOpen: { state: boolean; kind: 'delete' | 'unlink' };
  handleUnlink: (id: string) => void;
  handleDelete: () => void;
  handleCreateLoginEmail: (email: string, onClose: () => void) => void;
  handleOpenInviteUser: (state: boolean) => void;
  handleNavigateBack: () => void;
  handleOpenConfirmModal: (state: boolean, kind: 'delete' | 'unlink') => void;
  setErrors: React.Dispatch<React.SetStateAction<string[]>>;
  modalCreateMemberOpen: boolean;
  setModalCreateMemberOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getPartyResponse: UseQueryResult<GQLGetParty, unknown>;
};

const ContactEditScene: FC<ContactEditSceneProps> = ({
  t,
  loading,
  isDeletingParty,
  methods,
  errors,
  modalInvite,
  confirmModalOpen,
  handleUnlink,
  handleDelete,
  handleCreateLoginEmail,
  handleOpenInviteUser,
  handleNavigateBack,
  handleOpenConfirmModal,
  setErrors,
  modalCreateMemberOpen,
  setModalCreateMemberOpen,
  getPartyResponse,
}) => {
  const { t: tLocal } = useTranslation();
  const classes = useStyles();

  const [currentName, setCurrentName] = useState(getPartyResponse.data?.getParty?.name || '');
  const [currentUsername, setCurrentUsername] = useState(
    getPartyResponse.data?.getParty?.username || ''
  );

  const [previousPartyData, setPreviousPartyData] = useState(getPartyResponse.data);
  if (previousPartyData !== getPartyResponse.data) {
    setPreviousPartyData(getPartyResponse.data);
    if (getPartyResponse.data?.getParty) {
      setCurrentName(getPartyResponse.data.getParty.name || '');
      setCurrentUsername(getPartyResponse.data.getParty.username || '');
    }
  }

  const _id = methods.watch('_id') || '';
  const name = currentName;
  const isTitleCompany = methods.getValues('kind') === CONTACT_TITLE_COMPANY_KIND;
  const isTitleAgency = (methods.getValues('parties') || []).some(
    (party) => party.kind === CONTACT_TITLE_AGENCY
  );

  return loading ? (
    <BackdropLoading open loadingText={tLocal('contact:loadingText')} />
  ) : (
    <>
      <Grid container direction="column" className={classes.container}>
        <Grid item xs={12}>
          <Navigation t={t} name={currentName} handleNavigateBack={handleNavigateBack} />
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <Grid container direction="row">
              <Grid item xs={12}>
                <Grid container direction="row">
                  {isTitleCompany && (
                    <Grid item xs={12}>
                      <Alert severity="warning">{tLocal('contact:titleCompanyAlert')}</Alert>
                    </Grid>
                  )}

                  {!!errors.length && (
                    <Grid item xs={12}>
                      <Alert severity="error" classes={{ icon: classes.errorIconContainer }}>
                        {errors.map((error, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Typography key={`${error}-${index}`} variant="subtitle1">{error}</Typography>
                        ))}
                      </Alert>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Header
                      t={t}
                      name={currentName}
                      username={currentUsername}
                      isTitleCompany={isTitleCompany}
                      handleOpenConfirmModal={handleOpenConfirmModal}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider orientation="horizontal" />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} className={classes.formContainer}>
                <Grid
                  container
                  direction="column"
                  spacing={3}
                  className={classes.innerFormContainer}
                >
                  <Grid item xs={12}>
                    {getPartyResponse.data?.getParty && (
                      <ContactFormEdit
                        contact={getPartyResponse.data?.getParty}
                        refreshContacts={() => {
                          getPartyResponse.refetch();
                        }}
                        onNameChange={(newName) => setCurrentName(newName)}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <ModalInviteUser
        t={t}
        open={modalInvite}
        emails={methods.getValues('emails')}
        onClose={() => handleOpenInviteUser(false)}
        handleCreateLoginEmail={handleCreateLoginEmail}
      />

      {confirmModalOpen.state && (
        <DeleteConfirmationModal
          isOpen
          title={
            confirmModalOpen.kind === 'delete'
              ? t('users:dialog-delete-title')
              : t('users:dialog-unlink-title')
          }
          confirmationMessage={`${name}\n\n${
            confirmModalOpen.kind === 'delete'
              ? t('users:dialog-delete-description')
              : t('users:dialog-unlink-description')
          }`}
          confirmWord={confirmModalOpen.kind === 'delete' ? 'delete' : undefined}
          confirmIcon={confirmModalOpen.kind === 'delete' ? 'trash' : 'unlink'}
          buttonConfirmText={
            confirmModalOpen.kind === 'delete'
              ? t('users:dialog-delete-confirm-button')
              : t('users:dialog-unlink-confirm-button')
          }
          isRemoveLoading={confirmModalOpen.kind === 'delete' && isDeletingParty}
          onConfirm={() =>
            (confirmModalOpen.kind === 'delete' ? handleDelete() : handleUnlink(_id))}
          onClose={() => handleOpenConfirmModal(false, 'delete')}
        />
      )}

      <ModalCreateAssociation
        partyId={_id}
        isTitleCompany={isTitleCompany}
        isTitleAgency={isTitleAgency}
        open={modalCreateMemberOpen}
        onClose={() => setModalCreateMemberOpen(false)}
        onSuccess={() => {
          getPartyResponse.refetch();
        }}
        onError={(error) => {
          setErrors((prev) => [...prev, getContactErrorMessage(error, t)]);
        }}
      />
    </>
  );
};

export default ContactEditScene;
